import React, { useEffect, useRef, useState } from 'react'
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton,
  LinearProgress, Paper, Tab, Tabs, TextField, Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SendIcon from '@mui/icons-material/Send'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../auth/AuthContext'
import { Project, Step, Submission, AIInteraction } from '../../types'
import FormsWizard from '../../components/student/FormsWizard'


const STEP_LABELS_SCIENCE: Record<number, string> = {
  1: 'Select a Research Topic',
  2: 'Ask a Research Question',
  3: 'Gather Background Information',
  4: 'Formulate a Hypothesis',
  5: 'Design an Experiment',
  6: 'Conduct the Experiment and Record Data',
  7: 'Analyze Data and Draw Conclusions',
  8: 'Present Your Findings',
}

const STEP_LABELS_ENGINEERING: Record<number, string> = {
  1: 'Select a Research Topic',
  2: 'Identify a Problem',
  3: 'Gather Background Information',
  4: 'Specify Design Requirements',
  5: 'Design a Solution',
  6: 'Develop the Solution',
  7: 'Test and Record Performance',
  8: 'Present Your Solutions',
}

type StageTab = 'learn' | 'preapproval' | 'work' | 'submit' | 'journal'

export default function StepPage() {
  const { stepNumber }               = useParams<{ stepNumber: string }>()
  const { session }                  = useAuth()
  const navigate                     = useNavigate()
  const num                          = Number(stepNumber)
  const messagesEndRef               = useRef<HTMLDivElement>(null)

  const [project,      setProject]      = useState<Project | null>(null)
  const [step,         setStep]         = useState<Step | null>(null)
  const [submissions,  setSubmissions]  = useState<Submission[]>([])
  const [interactions, setInteractions] = useState<AIInteraction[]>([])
  const [loading,      setLoading]      = useState(true)
  const [tab,          setTab]          = useState<StageTab>('learn')

  // Work / submit state
  const [workContent,  setWorkContent]  = useState<Record<string, string>>({})
  const [submitting,   setSubmitting]   = useState(false)
  const [submitError,  setSubmitError]  = useState<string | null>(null)

  // Chat state (advisory mode)
  const [chatMsg,   setChatMsg]   = useState('')
  const [chatLoading, setChatLoading] = useState(false)

  // Latest submission
  const latest = submissions[0] ?? null

  useEffect(() => {
    async function load() {
      if (!session) return

      const { data: sp } = await supabase
        .from('student_profile')
        .select('student_id')
        .eq('user_id', session.user.id)
        .maybeSingle()
      if (!sp) { navigate('/'); return }

      const [{ data: projects }, { data: steps }] = await Promise.all([
        supabase
          .from('project')
          .select('*')
          .eq('student_id', sp.student_id)
          .eq('status', 'active')
          .limit(1),
        supabase
          .from('step')
          .select('*')
          .eq('step_number', num)
          .limit(1),
      ])

      const project = projects?.[0] ?? null
      const step    = steps?.[0] ?? null
      setProject(project)
      setStep(step)

      if (project && step) {
        const [{ data: subs }, { data: intrs }] = await Promise.all([
          supabase
            .from('submission')
            .select('*')
            .eq('project_id', project.project_id)
            .eq('step_id', step.step_id)
            .order('version_number', { ascending: false }),
          supabase
            .from('ai_interaction')
            .select('*')
            .eq('project_id', project.project_id)
            .eq('step_id', step.step_id)
            .order('timestamp'),
        ])
        setSubmissions(subs ?? [])
        setInteractions(intrs ?? [])

        // Pre-fill work content from latest submission if exists
        if (subs?.[0]?.content) {
          setWorkContent(subs[0].content as Record<string, string>)
        }
      }

      setLoading(false)
    }
    load()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, num])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [interactions])

  // Workspace fields — parsed from step.workspace_fields or default to a single textarea
  const fields: Array<{ key: string; label: string; multiline?: boolean }> = step
    ? Object.entries(step.workspace_fields as Record<string, any>).map(([key, val]) => ({
        key,
        label: typeof val === 'object' ? (val.label ?? key) : String(val),
        multiline: typeof val === 'object' ? val.multiline !== false : true,
      }))
    : [{ key: 'content', label: 'Your work', multiline: true }]

  async function handleSubmit() {
    if (!project || !step || !session) return
    setSubmitting(true)
    setSubmitError(null)

    try {
      const { data: sp } = await supabase
        .from('student_profile')
        .select('student_id')
        .eq('user_id', session.user.id)
        .maybeSingle()

      const { data: result, error: invokeErr } = await supabase.functions.invoke('score-submission', {
        body: {
          project_id: project.project_id,
          step_id:    step.step_id,
          student_id: sp?.student_id,
          content:    workContent,
        },
      })

      if (invokeErr || result?.error) {
        throw new Error(invokeErr?.message ?? result?.error ?? 'Scoring failed')
      }

      // Reload submissions
      const { data: subs } = await supabase
        .from('submission')
        .select('*')
        .eq('project_id', project.project_id)
        .eq('step_id', step.step_id)
        .order('version_number', { ascending: false })
      setSubmissions(subs ?? [])
      setTab('submit')
    } catch (e: any) {
      setSubmitError(e.message)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleAdvance() {
    if (!project || !step || !latest) return

    await supabase
      .from('submission')
      .update({
        advance_decision: latest.score && latest.score >= 85 ? 'advanced' : 'overridden_below_85',
        advanced_at:      new Date().toISOString(),
      })
      .eq('submission_id', latest.submission_id)

    await supabase
      .from('project')
      .update({ current_step: num + 1 })
      .eq('project_id', project.project_id)

    // Journal entry
    await supabase.from('journal_entry').insert({
      project_id:       project.project_id,
      student_id:       latest.student_id,
      step_id:          step.step_id,
      entry_type:       latest.score && latest.score >= 85 ? 'advance' : 'override',
      reference_id:     latest.submission_id,
      reference_table:  'submissions',
      content_snapshot: `Advanced from Step ${num} with score ${latest.score ?? 'N/A'}`,
      timestamp:        new Date().toISOString(),
    })

    navigate('/')
  }

  async function sendChat() {
    if (!chatMsg.trim() || !project || !step || !session) return
    setChatLoading(true)

    const userMsg = chatMsg.trim()
    setChatMsg('')

    // Log student message
    const { data: sp } = await supabase
      .from('student_profile')
      .select('student_id')
      .eq('user_id', session.user.id)
      .maybeSingle()

    await supabase.from('ai_interaction').insert({
      project_id:       project.project_id,
      student_id:       sp?.student_id,
      step_id:          step.step_id,
      submission_id:    null,
      role:             'student',
      message:          userMsg,
      prompt_version:   'v1',
      model_version:    'claude-sonnet-4-6',
      interaction_type: 'advisory',
    })

    // Refresh interactions
    const { data: intrs } = await supabase
      .from('ai_interaction')
      .select('*')
      .eq('project_id', project.project_id)
      .eq('step_id', step.step_id)
      .order('timestamp')
    setInteractions(intrs ?? [])

    // Call edge function for AI advisory response
    try {
      const { error: invokeErr } = await supabase.functions.invoke('advisory-chat', {
        body: {
          project_id: project.project_id,
          step_id:    step.step_id,
          student_id: sp?.student_id,
          message:    userMsg,
          history:    (intrs ?? []).map(i => ({ role: i.role, content: i.message })),
        },
      })
      if (!invokeErr) {
        const { data: intrs2 } = await supabase
          .from('ai_interaction')
          .select('*')
          .eq('project_id', project.project_id)
          .eq('step_id', step.step_id)
          .order('timestamp')
        setInteractions(intrs2 ?? [])
      }
    } catch { /* advisory is best-effort */ }

    setChatLoading(false)
  }

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
  if (!step)   return <Box sx={{ p: 4 }}><Typography>Step not found.</Typography></Box>

  const canAdvance  = latest !== null
  const score       = latest?.score ?? null
  const scoreColor  = score === null ? 'default' : score >= 85 ? 'success' : 'warning'
  const currentStep = project?.current_step ?? 1
  const isLocked    = num > currentStep
  const STEP_LABELS = project?.track === 'engineering_design' ? STEP_LABELS_ENGINEERING : STEP_LABELS_SCIENCE

  if (isLocked) {
    return (
      <Box sx={{ p: 4 }}>
        <IconButton onClick={() => navigate('/')} size="small" sx={{ mb: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Step {num}: {STEP_LABELS[num] ?? step.step_name}
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          Complete the previous steps first to unlock this one.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 4, maxWidth: 900 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton onClick={() => navigate('/')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Step {num}: {STEP_LABELS[num] ?? step.step_name}
        </Typography>
        {score !== null && (
          <Chip label={`${score}/100`} color={scoreColor} size="small" sx={{ ml: 1 }} />
        )}
      </Box>
      <Typography color="text.secondary" sx={{ mb: 3, ml: 5 }}>{step.description}</Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Learn" value="learn" />
        {num === 6 && <Tab label="Pre-Approval" value="preapproval" />}
        <Tab label="Work"  value="work" />
        <Tab label="Score" value="submit" />
        <Tab label="Journal" value="journal" />
      </Tabs>

      {/* ── LEARN ─────────────────────────────────────────────────── */}
      {tab === 'learn' && (
        <Box>
          {step.learn_content_url ? (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Review the lesson content for this step before starting your work.
              </Typography>
              <Button variant="contained" href={step.learn_content_url} target="_blank" rel="noopener noreferrer">
                Open Lesson Content
              </Button>
            </Box>
          ) : (
            <Paper sx={{ p: 3 }} variant="outlined">
              <Typography color="text.secondary">
                Lesson content for this step will be added by your instructor. You can proceed to the Work tab to get started.
              </Typography>
            </Paper>
          )}
          <Box sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={() => setTab('work')}>
              Go to Work →
            </Button>
          </Box>
        </Box>
      )}

      {/* ── PRE-APPROVAL (Step 6) ─────────────────────────────────── */}
      {tab === 'preapproval' && num === 6 && project && (
        <FormsWizard
          projectId={project.project_id}
          studentId={project.student_id}
          editable={currentStep >= 6}
        />
      )}

      {/* ── WORK ──────────────────────────────────────────────────── */}
      {tab === 'work' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Workspace */}
          <Paper sx={{ p: 3 }} variant="outlined">
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Your Workspace
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {fields.map(f => (
                <TextField
                  key={f.key}
                  label={f.label}
                  value={workContent[f.key] ?? ''}
                  onChange={e => setWorkContent(c => ({ ...c, [f.key]: e.target.value }))}
                  multiline={f.multiline !== false}
                  minRows={f.multiline !== false ? 3 : 1}
                  fullWidth
                />
              ))}
            </Box>
            {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting || Object.values(workContent).every(v => !v.trim())}
              >
                {submitting ? <CircularProgress size={20} color="inherit" /> : 'Submit for Scoring'}
              </Button>
              {canAdvance && (
                <Button
                  variant={score !== null && score >= 85 ? 'contained' : 'outlined'}
                  color={score !== null && score >= 85 ? 'success' : 'warning'}
                  onClick={handleAdvance}
                  disabled={num >= 8}
                >
                  {score !== null && score >= 85 ? 'Advance to Step ' + (num + 1) : 'Override & Advance'}
                </Button>
              )}
            </Box>
          </Paper>

          {/* AI Advisory Chat */}
          <Paper sx={{ p: 3 }} variant="outlined">
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Ask AI
            </Typography>
            <Box sx={{ maxHeight: 320, overflowY: 'auto', mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {interactions.length === 0 && (
                <Typography color="text.secondary" variant="body2">
                  Ask the AI for guidance on this step. Your conversation is logged in your journal.
                </Typography>
              )}
              {interactions.map(i => (
                <Box
                  key={i.interaction_id}
                  sx={{
                    alignSelf:   i.role === 'student' ? 'flex-end' : 'flex-start',
                    bgcolor:     i.role === 'student' ? 'primary.50' : 'grey.100',
                    border:      '1px solid',
                    borderColor: i.role === 'student' ? 'primary.200' : 'divider',
                    borderRadius: 2,
                    px: 2, py: 1,
                    maxWidth: '80%',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                    {i.role === 'student' ? 'You' : 'AI'}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{i.message}</Typography>
                </Box>
              ))}
              {chatLoading && (
                <Box sx={{ alignSelf: 'flex-start', p: 1 }}>
                  <CircularProgress size={18} />
                </Box>
              )}
              <div ref={messagesEndRef} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                value={chatMsg}
                onChange={e => setChatMsg(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat() } }}
                placeholder="Ask a question about this step..."
                fullWidth
                size="small"
                disabled={chatLoading}
              />
              <IconButton onClick={sendChat} disabled={!chatMsg.trim() || chatLoading} color="primary">
                <SendIcon />
              </IconButton>
            </Box>
          </Paper>
        </Box>
      )}

      {/* ── SCORE ─────────────────────────────────────────────────── */}
      {tab === 'submit' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {submissions.length === 0 ? (
            <Paper sx={{ p: 3 }} variant="outlined">
              <Typography color="text.secondary">
                No submissions yet. Complete the Work tab and submit for scoring.
              </Typography>
            </Paper>
          ) : (
            submissions.map((sub, i) => (
              <Paper key={sub.submission_id} variant="outlined" sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Version {sub.version_number}
                      {i === 0 && <Chip label="Latest" size="small" sx={{ ml: 1 }} />}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(sub.submitted_at).toLocaleString()}
                    </Typography>
                  </Box>
                  {sub.score !== null ? (
                    <Chip
                      label={`${sub.score}/100`}
                      color={sub.score >= 85 ? 'success' : 'warning'}
                    />
                  ) : (
                    <Chip label="Scoring..." color="default" />
                  )}
                </Box>

                {sub.score !== null && (
                  <Box sx={{ mb: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={sub.score}
                      color={sub.score >= 85 ? 'success' : 'warning'}
                      sx={{ height: 8, borderRadius: 4, mb: 0.5 }}
                    />
                    {sub.score >= 85 ? (
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                        ✓ Meets the minimum standard (85+)
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="warning.main" sx={{ fontWeight: 500 }}>
                        Below 85 — review the feedback and resubmit
                      </Typography>
                    )}
                  </Box>
                )}

                {sub.ai_feedback && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>AI Feedback</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{sub.ai_feedback}</Typography>
                  </Box>
                )}

                {sub.ai_items_for_improvement && (sub.ai_items_for_improvement as string[]).length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Items for Improvement</Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      {(sub.ai_items_for_improvement as string[]).map((item, j) => (
                        <Typography component="li" variant="body2" key={j}>{item}</Typography>
                      ))}
                    </Box>
                  </Box>
                )}

                {i === 0 && sub.score !== null && num < 8 && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button
                      variant={sub.score >= 85 ? 'contained' : 'outlined'}
                      color={sub.score >= 85 ? 'success' : 'warning'}
                      onClick={handleAdvance}
                    >
                      {sub.score >= 85 ? `Advance to Step ${num + 1}` : `Override & Advance (score below 85)`}
                    </Button>
                  </Box>
                )}
              </Paper>
            ))
          )}
        </Box>
      )}

      {/* ── JOURNAL ───────────────────────────────────────────────── */}
      {tab === 'journal' && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your research journal for this step — all submissions, scores, and AI interactions.
          </Typography>
          {interactions.length === 0 && submissions.length === 0 ? (
            <Paper sx={{ p: 3 }} variant="outlined">
              <Typography color="text.secondary">No journal entries yet.</Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {submissions.map(sub => (
                <Paper key={sub.submission_id} variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(sub.submitted_at).toLocaleString()} · Submission v{sub.version_number}
                    {sub.score !== null && ` · Score: ${sub.score}/100`}
                  </Typography>
                  {sub.ai_feedback && (
                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                      {sub.ai_feedback}
                    </Typography>
                  )}
                </Paper>
              ))}
              {interactions.map(i => (
                <Paper key={i.interaction_id} variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(i.timestamp).toLocaleString()} · {i.role === 'student' ? 'You' : 'AI'}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>{i.message}</Typography>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}
